import { useCallback, useMemo } from 'react';

import type {
  AirdropCampaignAllocations,
  AirdropCampaignAllocationsDataset,
} from '@infinex/types';
import { useMappedDataset } from '@infinex/user';

import { filterAirdropCampaignAllocations } from './utils';

import type { AirdropCampaignAllocationsFilters } from '../../types';

export interface UseAirdropCampaignAllocationsResult {
  /** All airdrop campaign allocations for the user */
  data: AirdropCampaignAllocations[] | undefined;
  /** Dataset status */
  status: 'idle' | 'loading' | 'loaded' | 'error';
}

/**
 * Hook to get filtered airdrop campaign allocations.
 *
 * This replaces `useGetAirdropCampaignAllocationsQuery(filters)` with dataset-based approach.
 *
 * @example
 * ```tsx
 * // Get only claimable airdrops
 * const { data, status } = useFilteredAirdropCampaignAllocations({
 *   isClaimable: true
 * });
 *
 * // Get specific campaign
 * const { data } = useFilteredAirdropCampaignAllocations({
 *   campaignKey: 'ethena_s2'
 * });
 *
 * // Get by status
 * const { data } = useFilteredAirdropCampaignAllocations({
 *   claimStatus: ['claimed', 'started']
 * });
 * ```
 */
export function useFilteredAirdropCampaignAllocations(
  filters?: AirdropCampaignAllocationsFilters
): UseAirdropCampaignAllocationsResult {
  // Use callback to ensure stable reference for useMappedDataset
  const mapFn = useCallback(
    (dataset: AirdropCampaignAllocationsDataset) => {
      return filterAirdropCampaignAllocations(dataset, filters);
    },
    [filters]
  );

  const dataset = useMappedDataset('airdropCampaignAllocations', mapFn);

  return useMemo(() => {
    return {
      data: dataset.data?.airdropCampaignAllocations,
      status: dataset.status,
    };
  }, [dataset]);
}
